import { CONTACT_EMAIL } from "../../constants/brand";
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Grid, Link, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MarkunreadOutlinedIcon from '@mui/icons-material/MarkunreadOutlined';
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import styles from '../../Styles/ComponentStyles/RegistrationSuccess/RegistrationSuccess.module.scss';
import newRBLogoForWhiteBg from '../../assets/Images/mar-lockup.svg';

const HELPFUL_TIPS = [
    "Check your spam/junk folder if you don't receive the verification email within a few minutes",
    "Verification links may expire after 24 hours for security reasons",
    "If you entered an incorrect email address, please register again with the correct one",
];

const RegistrationSuccess = () => {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    return (
        <Grid
            container
            justifyContent="center"
            alignItems="center"
            className={styles.pageWrapper}
        >
            <Grid item xs={12} sm={10} md={6} lg={5} xl={5} >
                <Box
                    component="img"
                    alt="Logo"
                    src={newRBLogoForWhiteBg}
                    height="30px"
                    sx={{ display: 'block', mx: 'auto', mb: '30px' }}
                />
                <Box className={styles.card}>

                    {/* ── Header ─────────────────────────────── */}
                    <Box className={styles.header}>
                        <Grid container direction="column" alignItems="center" gap={0}>
                            <Grid item>
                                <Box className={styles.checkCircle}>
                                    <CheckCircleOutlineIcon sx={{ color: '#3B78CE', fontSize: 40 }} />
                                </Box>
                            </Grid>
                            <Grid item>
                                <Typography className={styles.headerTitle}>
                                    Thank You for Registering!
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Typography className={styles.headerSubtitle}>
                                    Your registration has been submitted successfully. Please complete the following steps to activate your account.
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* ── Body ───────────────────────────────── */}
                    <Box className={styles.body}>
                        <Grid container direction="column" gap={2.5}>

                            {/* Step 1 – Verify Email */}
                            <Grid item xs={12}>
                                <Box className={styles.verifyStepCard}>
                                    <Grid container gap={2} alignItems="flex-start" wrap="nowrap">
                                        <Grid item>
                                            <Box className={`${styles.stepIconBox} ${styles.stepIconBoxBlue}`}>
                                                <MarkunreadOutlinedIcon sx={{ color: '#ffffff', fontSize: 22 }} />
                                            </Box>
                                        </Grid>
                                        <Grid item xs>
                                            <Grid container alignItems="center" gap={1} sx={{ mb: '4px' }}>
                                                <Grid item>
                                                    <Typography className={styles.stepTitle}>
                                                        Verify Your Email Address
                                                    </Typography>
                                                </Grid>
                                                <Grid item>
                                                    <span className={styles.badgeActionRequired}>Action Required</span>
                                                </Grid>
                                            </Grid>
                                            <Typography className={styles.stepDescription}>
                                                We've sent a verification link to your registered email address. Please check your inbox and verify your email to continue.
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>

                            {/* Step 2 – Admin Approval */}
                            <Grid item xs={12}>
                                <Box className={styles.adminApprovalStepCard}>
                                    <Grid container gap={2} alignItems="flex-start" wrap="nowrap">
                                        <Grid item>
                                            <Box className={`${styles.stepIconBox} ${styles.stepIconBoxDark}`}>
                                                <GppGoodOutlinedIcon sx={{ color: '#ffffff', fontSize: 22 }} />
                                            </Box>
                                        </Grid>
                                        <Grid item xs>
                                            <Grid container alignItems="center" gap={1} sx={{ mb: '4px' }}>
                                                <Grid item>
                                                    <Typography className={styles.stepTitle}>
                                                        Admin Approval
                                                    </Typography>
                                                </Grid>
                                                <Grid item>
                                                    <span className={styles.badgePending}>Pending</span>
                                                </Grid>
                                            </Grid>
                                            <Typography className={styles.stepDescription}>
                                                Once your email is verified, our admin team will review and approve your registration request.
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>

                            {/* Important Information */}
                            <Grid item xs={12}>
                                <Box className={styles.importantBox}>
                                    <Grid container gap={1.5} alignItems="flex-start" wrap="nowrap">
                                        <Grid item>
                                            <Box className={styles.importantIconBox}>
                                                <ErrorOutlineOutlinedIcon sx={{ color: '#ffffff', fontSize: 20 }} />
                                            </Box>
                                        </Grid>
                                        <Grid item xs>
                                            <Typography className={styles.importantTitle}>
                                                Important Information
                                            </Typography>
                                            <Typography className={styles.importantCannotLoginText}>
                                                You will not be able to log in until:
                                            </Typography>
                                            <Box>
                                                <Box className={styles.importantBullet}>
                                                    <span className={styles.importantBulletArrow}>→</span>
                                                    <Typography className={styles.importantBulletText}>
                                                        Your email address is verified
                                                    </Typography>
                                                </Box>
                                                <Box className={styles.importantBullet}>
                                                    <span className={styles.importantBulletArrow}>→</span>
                                                    <Typography className={styles.importantBulletText}>
                                                        Your account is approved by the admin team
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Typography className={styles.importantNote}>
                                                After approval, you'll receive a confirmation email and your portal access will be activated.
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>

                            {/* Helpful Tips */}
                            <Grid item xs={12}>
                                <Box className={styles.helpfulTipsBox}>
                                    <Typography className={styles.helpfulTipsTitle}>
                                        Helpful Tips
                                    </Typography>
                                    {HELPFUL_TIPS.map((tip, index) => (
                                        <Box key={index} className={styles.helpfulTipsBulletItem}>
                                            <Box className={styles.helpfulTipsDot} />
                                            <Typography className={styles.helpfulTipsText}>
                                                {tip}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Grid>

                            {/* Verify Email Button */}
                            <Grid item xs={12}>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    size="large"
                                    className={styles.verifyEmailBtn}
                                    onClick={() => navigate('/login')}
                                >
                                    Back to Login
                                </Button>
                            </Grid>

                            {/* Footer */}
                            <Grid item xs={12}>
                                <Typography className={styles.footerText}>
                                    Need help? Contact our support team at{' '}
                                    <Link
                                        href={`mailto:${CONTACT_EMAIL}`}
                                        className={styles.footerEmail}
                                    >
                                        {CONTACT_EMAIL}
                                    </Link>
                                </Typography>
                            </Grid>

                        </Grid>
                    </Box>

                </Box>
            </Grid>
        </Grid>
    );
};

export default RegistrationSuccess;